import { CSSProperties, useEffect } from 'react';
import CardSymphony from 'elements/CardSymphony';
import OptionsSymphony from 'elements/OptionsSymphony';
import useCampaign, { CampaignGoal } from 'Hooks/useCampaign';
import { clsx } from 'modules/Utils';
import ButtonSymphony from 'elements/ButtonSymphony';
import RecordSearchView from 'elements/RecordSearchView';
import { RecordDetails } from 'modules/Types';
import useScreen from 'Hooks/useScreen';
import LinkSelectorCampaignView from './LinkSelectorCampaignView';
import DebugBox from 'components/shareable/DebugBox';

export enum CAMPAIGN_GOAL {
    BOOST_VIEWS = 'boost_views',
    BOOST_PROFILE_VISITS = 'boost_profile_visits',
}

export const GOAL_SELECTOR_GOAL_OPTIONS: Record<CAMPAIGN_GOAL, CampaignGoal> = {
    [CAMPAIGN_GOAL.BOOST_VIEWS]: {
        id: "boost_views",
        icon: 'growInstagramEngagement',
        label: "Boost IG Post Views",
        subtype: 'video',
        conversionGoal: "Ads will optimize to boost views and likes on your IG Post.",
        description: "Increase views on an Instagram post, to increase your overall reach. Best used when your goal is to increase engagement on a post",
        recommendation: "Ideal for getting cost-effective views on a video, not primarily for growing followers.",
        tips: "Optimize for cost-effectiveness to maximize views and likes. While not the most efficient for increasing followers, it can significantly extend your reach with sufficient budget.",
        whenToUse: [
            "When you have a Instagram video that you want to push for maximum exposure.",
            "Best for getting affordable views on a video, not primarily for growing followers.",
            "To boost engagement and potentially trigger the Instagram algorithm for wider organic reach."
        ],
        setupNeeded: [
            "Access to your Instagram Ads account.",
            "An Instagram post to promote."
        ],
        conversionType: "view",
        averageConversionCost: 0.01,
        available: true,
    },
    [CAMPAIGN_GOAL.BOOST_PROFILE_VISITS]: {
        id: "boost_profile_visits",
        icon: 'instagramProfileVisits',
        label: "Boost IG Profile Visits",
        subtype: 'profile',
        conversionGoal: "Ads will optimize to increase profile visits.",
        description: "Optimize for more visits to your profile, resulting in potentially more followers. Best used when your goal is to increase followers or boost your reach.",
        recommendation: "Focuses on acquiring high-intent followers likely to engage with your content.",
        tips: "May be more costly per action but yields followers with a higher chance of engagement. Avoid if your primary goal is to gain cheap views rather than valuable followers.",
        whenToUse: [
            "When looking to grow your Instagram profile with new, high-quality followers.",
            "To increase your overall Instagram presence and potential for viral content."
        ],
        setupNeeded: [
            "Access to your Instagram Ads account.",
            "A compelling Instagram video to attract potential followers."
        ],
        conversionType: "profile visit",
        averageConversionCost: 0.20,
        available: true,
    }
}

interface GoalSelectorProps {
    className?: string;
    style?: CSSProperties;
    goalOptions: CampaignGoal[];
}

const GoalSelector = ({
    className,
    style = {},
    goalOptions = [],
}: GoalSelectorProps) => {
    const { cssCalc } = useScreen();
    const {
        campaignGoal,
        setCampaignGoal,
        linksToPromote,
        setLinksToPromote,
        setSelectedSongToPromote
    } = useCampaign();

    useEffect(() => {
        let defaultGoal = goalOptions[0];
        if (campaignGoal) {
            defaultGoal = campaignGoal;
        }
        setCampaignGoal(defaultGoal);
    }, []);

    const handleSelectSong = (recordDetails: RecordDetails) => {
        console.log('brandContent', recordDetails);
        setSelectedSongToPromote(recordDetails);
    }

    const handleSelectGoal = (id: string | number) => {
        const foundOption = goalOptions.find(option => option.id === id);
        if (foundOption) {
            setCampaignGoal(foundOption);
        }
    }

    const renderGoalOptions = () => {
        return (
            <div
                className="goalSelectorContainer"
                style={sty.goalSelectorContainer}
            >
                <OptionsSymphony
                    className="goalSelectorOptions"
                    options={goalOptions}
                    value={campaignGoal?.id || ''}
                    handleOptionSelected={handleSelectGoal}
                />
                {renderLinkToPromote()}
            </div>
        )
    }
    const renderLinkToPromote = () => {
        if (campaignGoal?.id === 'promote_a_link') {
            /** Current link value from the first item in linksToPromote array */
            const currentLink = linksToPromote && linksToPromote.length > 0 ? linksToPromote[0] : '';

            /** Updates the first link in the linksToPromote array */
            const updateLink = (value: string) => {
                if (linksToPromote && linksToPromote.length > 0) {
                    const updatedLinks = [...linksToPromote];
                    updatedLinks[0].url = value;
                    setLinksToPromote(updatedLinks);
                    // Assuming there's a setter for linksToPromote in the useCampaign hook
                    // This would need to be added to the destructured values from useCampaign
                } else {
                    // If no links exist yet, create a new array with this link
                    // This would need the setter from useCampaign
                }
            };

            return (
                <div className="linkToPromoteContainer"
                    style={{
                        marginTop: 14
                    }}>
                    <LinkSelectorCampaignView
                        className="linkToPromoteInput"
                        placeholder="https://www.example.com"
                    />
                </div>
            )
        }

        return null;
    }
    const renderGoalBackButton = () => {
        return (
            <ButtonSymphony
                className="goalBackButton"
                onClick={() => setCampaignGoal(null)}
                variant="text"
            >
                Back
            </ButtonSymphony>
        )
    }

    if (campaignGoal?.id === 'promote_a_song') {
        return (
            <div className="promoteASongContainer">
                {renderGoalBackButton()}
                <CardSymphony
                    className="linkToPromoteHeader"
                    title="Promote a Song"
                    subtitle="Select the song you want to promote"
                    isNoPadding
                    style={{ marginBottom: 24 }}
                />
                <RecordSearchView
                    className="promoteASongRecordSearchView"
                    handleSelectRecord={handleSelectSong}
                    options={['single', 'project']}
                    maxHeight={cssCalc(72 + 72 + 64 + 246)}
                />
            </div>
        )
    }

    return (
        <CardSymphony
            className={clsx('goalSelectorComponent', className)}
            style={style}
            title="What do you want to boost?"
            subtitle="Select your campaign goal and Symphony's ads will configure to maximize your budget."
            size="small"
            isNoPadding
        >
            {renderGoalOptions()}
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    goalOptionCard: {
        backgroundColor: 'white',
        borderRadius: 12,
        border: '1px solid #EDECF2',
        padding: 16,
        transition: 'background-color 0.3s'
    },
    selectedCard: {
        backgroundColor: '#F5EBFF'
    },
    icon: {
        width: 40,
        height: 40,
        objectFit: 'contain'
    }
}

export default GoalSelector;